import {
  OAuth2Client,
  MemoryOAuth2TokenStorage,
  type OAuth2Tokens,
  type OAuth2TokenStorage
} from '@docyrus/api-client';

import { type DocyrusAuthConfig, type OpenAuthSessionFn } from '../types';

import {
  DEFAULT_API_URL,
  DEFAULT_OAUTH_SCOPES
} from '../constants';

/**
 * React Native OAuth2 authorization code flow with PKCE.
 *
 * Instead of browser redirects (`window.location.href`), this class uses
 * an injected `openAuthSession` function (e.g., expo-web-browser's
 * `WebBrowser.openAuthSessionAsync`) that opens an in-app browser,
 * waits for the callback, and returns the callback URL directly.
 *
 * No DOM, no localStorage, no window.location — fully RN compatible.
 */
export class ReactNativeOAuth2Auth {
  private oauth2Client: OAuth2Client;
  private storage: OAuth2TokenStorage;
  private openAuthSession: OpenAuthSessionFn;
  private redirectUri: string;
  constructor(config: DocyrusAuthConfig) {
    const apiUrl = config.apiUrl ?? DEFAULT_API_URL;

    if (!config.clientId) {
      throw new Error(
        'DocyrusAuthProvider: "clientId" is required for React Native mode.'
      );
    }

    if (!config.nativeRedirectUri) {
      throw new Error(
        'DocyrusAuthProvider: "nativeRedirectUri" is required for React Native mode. '
        + 'Set it to your app\'s deep link URI (e.g., "myapp://auth/callback").'
      );
    }

    if (!config.openAuthSession) {
      throw new Error(
        'DocyrusAuthProvider: "openAuthSession" is required for React Native mode. '
        + 'Pass WebBrowser.openAuthSessionAsync from expo-web-browser.'
      );
    }

    const scopes = config.scopes ?? DEFAULT_OAUTH_SCOPES;

    this.redirectUri = config.nativeRedirectUri;
    this.openAuthSession = config.openAuthSession;

    this.storage = config.tokenStorage ?? new MemoryOAuth2TokenStorage();

    this.oauth2Client = new OAuth2Client({
      baseURL: apiUrl,
      clientId: config.clientId,
      redirectUri: this.redirectUri,
      defaultScopes: scopes,
      usePKCE: true,
      tokenStorage: this.storage,
      cryptoProvider: config.cryptoProvider
    });
  }
  /**
   * Initiate login by opening an in-app browser for OAuth2 authorization.
   * Unlike the browser version, this does NOT navigate the app — it opens
   * a modal browser and waits for the callback URL.
   */
  async initiateLogin(): Promise<OAuth2Tokens> {
    const { url } = await this.oauth2Client.getAuthorizationUrl();

    const result = await this.openAuthSession(url, this.redirectUri);

    if (result.type !== 'success' || !result.url) {
      throw new Error(
        `Authentication was ${result.type === 'cancel' ? 'cancelled by the user' : 'dismissed'}`
      );
    }

    // Exchange the authorization code for tokens
    return this.oauth2Client.handleCallback(result.url);
  }
  /** Get currently stored tokens. */
  async getStoredTokens(): Promise<OAuth2Tokens | null> {
    return this.oauth2Client.getTokens();
  }
  /** Persist externally obtained tokens for immediate authenticated use. */
  async setTokens(tokens: OAuth2Tokens): Promise<void> {
    await Promise.resolve(this.storage.setTokens(tokens));
  }
  /** Check if the stored token is expired (with 60s buffer). */
  async isTokenExpired(): Promise<boolean> {
    return this.oauth2Client.isTokenExpired();
  }
  /** Refresh the access token using the stored refresh token. */
  async refreshToken(): Promise<OAuth2Tokens> {
    return this.oauth2Client.refreshAccessToken();
  }
  /** Get a valid access token, refreshing if needed. */
  async getValidAccessToken(): Promise<string> {
    return this.oauth2Client.getValidAccessToken();
  }
  /** Logout: revoke token and clear storage. */
  async logout(): Promise<void> {
    return this.oauth2Client.logout();
  }
}
